import { useCallback, useEffect, useState } from "react";
import { useRealtime } from "src/hooks";
import { fetchAttentionList } from "./api";
import { attentionChangedEventName, IAttentionChangedMessage, IAttentionList } from "./types";

/**
 * How long a broadcast refetch is spread over. An instance scoped message reaches
 * every connected client at once, so without this they would all ask the server
 * together the moment anything is published.
 */
const instanceRefetchSpreadMs = 20000;

/**
 * How long a list is reused before anyone asking again causes a request. The
 * server caches for the same length of time, so a shorter window here would only
 * buy round trips that return the same rows.
 */
const freshForMs = 60000;

export interface IUseAttentionResult {
    list: IAttentionList | null;
    isLoading: boolean;
    hasFailed: boolean;
    reload: () => void;
}

/**
 * One list, shared by everyone who asks for it.
 *
 * The bell, the block inside it and the page are three components that each want
 * the same rows, and a plain hook would give each of them its own request. This
 * holds the list outside React and hands every caller the same copy, so mounting
 * a second reader costs nothing and a signal refreshes all of them at once.
 */
const store = {
    list: null as IAttentionList | null,
    fetchedAt: 0,
    isLoading: false,
    hasFailed: false,
    inFlight: null as Promise<void> | null,
};

const subscribers = new Set<() => void>();

function notify(): void {
    subscribers.forEach((subscriber) => subscriber());
}

/**
 * @param force Ask again even if the list we hold is still fresh. Used when
 * something has told us it changed, which is exactly when "fresh" is wrong.
 */
function load(force: boolean): Promise<void> {
    if (!force && store.inFlight) {
        return store.inFlight;
    }

    if (!force && store.list && Date.now() - store.fetchedAt < freshForMs) {
        return Promise.resolve();
    }

    store.isLoading = store.list === null;
    store.hasFailed = false;
    notify();

    const request = fetchAttentionList()
        .then((list) => {
            store.list = list;
            store.fetchedAt = Date.now();
            store.hasFailed = false;
        })
        .catch(() => {
            // The list we already hold is kept. A momentary failure should not
            // empty the bell, which would read as "you are done".
            store.hasFailed = true;
        })
        .finally(() => {
            store.isLoading = false;
            store.inFlight = null;
            notify();
        });

    store.inFlight = request;

    return request;
}

/**
 * Only for tests, which would otherwise see one another's rows.
 */
export function resetAttentionStore(): void {
    store.list = null;
    store.fetchedAt = 0;
    store.isLoading = false;
    store.hasFailed = false;
    store.inFlight = null;
}

/**
 * Loads the current person's attention list and keeps it current.
 *
 * The server tells us when something may have changed but sends no rows, so the
 * only thing to do on a signal is ask again. That keeps the message cheap enough
 * to broadcast and means a tab nobody is looking at costs nothing until it is.
 */
function useAttention(): IUseAttentionResult {
    const [, setVersion] = useState<number>(0);

    const { subscribe, unsubscribe } = useRealtime();

    useEffect(() => {
        const onStoreChanged = (): void => setVersion((version) => version + 1);

        subscribers.add(onStoreChanged);

        load(false);

        return () => {
            subscribers.delete(onStoreChanged);
        };
    }, []);

    useEffect(() => {
        let timeout: number | undefined;

        const onChanged = (message?: IAttentionChangedMessage): void => {
            window.clearTimeout(timeout);

            if (message?.scope === "instance") {
                timeout = window.setTimeout(() => load(true), Math.random() * instanceRefetchSpreadMs);

                return;
            }

            // This person just did something. Waiting would show them a row they
            // have already dealt with.
            load(true);
        };

        subscribe(attentionChangedEventName, onChanged);

        return () => {
            window.clearTimeout(timeout);
            unsubscribe(attentionChangedEventName, onChanged);
        };
    }, [subscribe, unsubscribe]);

    const reload = useCallback(() => {
        load(true);
    }, []);

    return {
        list: store.list,
        isLoading: store.isLoading,
        hasFailed: store.hasFailed,
        reload,
    };
}

export default useAttention;
